﻿<template>
  <Form as="div" ref="form" v-slot="{ errors }">
    <h1 v-html="personalInformationModel.title" />
    <article class="flow-group">
      <div class="control">
        <strong v-html="personalInformationModel.phoneNumberLabel" />
        <Field
          v-model="phoneNumber"
          v-slot="{ field }"
          :rules="'required|numeric|digits:8'"
          name="telephone"
          :label="personalInformationModel.phoneNumberLabel"
        >
          <base-text-input
            v-bind="field"
            :placeholder="personalInformationModel.phoneNumberPlaceholder"
            name="telephone"
            :label="personalInformationModel.phoneNumberLabel"
            :errors="errors"
          />
        </Field>
        <ValidationError
          name="telephone"
          :overrides="{ required: personalInformationModel.phoneMissingValidationMessage }"
        />
      </div>
    </article>
    <article class="flow-group">
      <div class="control">
        <strong v-html="personalInformationModel.emailLabel" />
        <Field
          v-model="email"
          rules="required|email"
          name="email"
          v-slot="{ field }"
          :label="personalInformationModel.emailLabel"
        >
          <base-text-input
            name="email"
            v-bind="field"
            :placeholder="personalInformationModel.emailPlaceholder"
            :errors="errors"
          />
        </Field>
        <ValidationError
          name="email"
          :overrides="{ required: personalInformationModel.emailMissingValidationMessage }"
        />
      </div>
    </article>
    <article class="flow-group push-down">
      <div class="control">
        <strong v-html="personalInformationModel.cprLabel" />
        <cpr-number-field
          v-model="cprNumber"
          :name="personalInformationModel.cprLabel"
          :label="personalInformationModel.cprLabel"
          :placeholder="personalInformationModel.cprPlaceholder"
          :errors="errors"
        />
      </div>
    </article>
    <article class="flow-group checkbox-inline">
      <div class="control">
        <Field
          v-slot="{ field }"
          :model-value="cprConsent"
          :rules="'required'"
          name="cprConsentYes"
          type="checkbox"
          :value="true"
          :unchecked-value="false"
          @change="cprConsent = $event.target.checked"
        >
          <input
            id="cprConsentYes"
            v-bind="field"
            name="cprConsentYes"
            type="checkbox"
            :value="true"
          />
          <label
            for="cprConsentYes"
            class="large"
            data-test="personal-information-input-cpr-consent-yes"
            v-html="personalInformationModel.cprConsentYesLabel"
          />
        </Field>
        <input id="cprConsentNo" v-model="noToCprConsent" name="cprConsentNo" type="checkbox" />
        <label
          for="cprConsentNo"
          class="large"
          data-test="personal-information-input-cpr-consent-no"
          v-html="personalInformationModel.cprConsentNoLabel"
        />
        <div class="push-up" v-html="personalInformationModel.cprConsentDescription" />
        <p>
          <a
            :href="personalInformationModel.personalDataPolicyLink.url"
            target="_blank"
            v-html="personalInformationModel.personalDataPolicyLink.text"
          />
        </p>
        <info-box
          v-if="errors['cprConsentYes'] !== undefined"
          info-box-class="info-box-warning-alt"
          arrow-align="left"
          v-html="personalInformationModel.cprConsentMissingValidationMessage"
        />
      </div>
    </article>
    <cpr-consent-modal v-model="showNoToCPRConsentModal" />
  </Form>
</template>

<script lang="ts">
import axios from 'axios';
import { mapState } from 'vuex';
import { defineComponent } from 'vue';
import CprNumberField from '@/components/UI/CprNumberField.vue';
import BaseTextInput from '@/components/Forms/BaseTextInput.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import InfoBox from '@/components/UI/InfoBox.vue';
import CprConsentModal from '../components/CprConsentModal.vue';
import { CreateMemberStoreState } from '@/store/modules/interfaces/CreateMemberStoreState';
import Utility from '@/common/UtilityFunctions.js';
import { FormContextKey } from 'vee-validate';
import { inject } from 'vue';

declare module 'vue/types/vue' {
  interface Vue {
    showNoToCPRConsentModal: boolean;
  }
}

export default defineComponent({
  name: 'PersonalInformation',
  components: {
    BaseTextInput,
    ValidationError,
    CprConsentModal,
    CprNumberField,
    InfoBox
  },
  setup() {
    return { form: inject(FormContextKey, undefined) };
  },
  async beforeRouteLeave(to, from, next) {
    await this.$refs.form.validate();
    if (Object.keys(this.$refs.form.getErrors()).length == 0) {
      await axios
        .post('/api/eligibility/setmemberdata/', this.memberRootDataTemplateModel)
        .then(() => next())
        .catch((err) => {
          Utility.logError(err, this);
        });
    } else next(false);
  },
  data() {
    return {
      showNoToCPRConsentModal: false
    };
  },
  computed: {
    ...mapState('createMember', {
      personalInformationModel: (state: any) =>
        (state as CreateMemberStoreState).vueModel.stepOnePersonalInformation,
      nextButtonLabel: (state: any) =>
        (state as CreateMemberStoreState).vueModel.sharedFields.nextButtonLabel,
      backButtonLabel: (state: any) =>
        (state as CreateMemberStoreState).vueModel.sharedFields.backButtonLabel,
      stepTwoPath: (state: any) => (state as CreateMemberStoreState).stepTwoPath,
      memberRootDataTemplateModel: (state: any) =>
        (state as CreateMemberStoreState).vueModel.memberRootDataTemplateModel
    }),
    cprNumber: {
      get() {
        return this.$store.state.createMember.vueModel.memberRootDataTemplateModel.cprNumber;
      },
      set(value) {
        this.$store.commit('createMember/setCprNumber', value);
      }
    },
    cprConsent: {
      get() {
        return this.$store.state.createMember.vueModel.memberRootDataTemplateModel.cprConsent;
      },
      set(value: any) {
        this.$store.commit('createMember/setCprConsent', value ? value : null);
      }
    },
    noToCprConsent: {
      get() {
        return this.cprConsent === null ? null : !this.cprConsent;
      },
      set(value: boolean) {
        this.$store.commit('createMember/setCprConsent', value ? !value : null);
        this.showNoToCPRConsentModal = value;
      }
    },
    email: {
      get() {
        return this.$store.state.createMember.vueModel.memberRootDataTemplateModel.email;
      },
      set(value: any) {
        this.$store.commit('createMember/setEmail', value);
      }
    },
    phoneNumber: {
      get() {
        return this.$store.state.createMember.vueModel.memberRootDataTemplateModel.phoneNumber;
      },
      set(value: any) {
        this.$store.commit('createMember/setPhoneNumber', value);
      }
    },
  },
  mounted() {
    this.$store.dispatch('createMember/setButtons', {
      prevButton: {
        path: null,
        class: null,
        label: this.backButtonLabel,
        redirectTo: null
      },
      nextButton: {
        path: this.stepTwoPath,
        class: 'primary',
        label: this.nextButtonLabel,
        redirectTo: null
      }
    });
    this.$emit('component-mounted', 1);
    Utility.scrollToTop();
  },
  methods: {}
});
</script>
